import "../lib/firebase-client"; // side-effect: ensures Firebase app is initialized
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  deleteDoc,
  setDoc,
  updateDoc,
  increment,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getOrgParent } from './orgDataHelper';
import moment from 'moment';

const db = getFirestore();

// Firestore collection names
const ENGINES = 'engines';
const GA_DATA = 'gaData';
const WEBSITES = 'websites';

// Firestore subcollection names
const SERPS = 'serps';
const KEYWORDS = 'keywords';

// Firestore collection attributes
const WEBSITE_ID = 'websiteId';

export const getAsanaToken = async (id) => {
  const asanaIntegration = await getDoc(doc(db, 'integrations', id));
  const data = asanaIntegration.data();
  return data.token.access_token;
};

export const deleteEngineFromFirestore = async (engineId) => {
  try {
    await deleteDocumentFromCollection(ENGINES, engineId);
    await deleteSubcollectionFromCollection(ENGINES, engineId, SERPS);
    return true;
  } catch (e) {
    return false;
  }
};

export const deleteWebsiteFromFirestore = async (websiteId) => {
  await deleteDocumentsFromCollection(ENGINES, WEBSITE_ID, websiteId);
  await deleteDocumentsFromCollection(GA_DATA, WEBSITE_ID, websiteId);
  await deleteDocumentFromCollection(WEBSITES, websiteId);
};

export const deleteDocumentsFromCollection = async (
  collectionName,
  attribute,
  value
) => {
  try {
    let collectionId = null;
    const q = query(collection(db, collectionName), where(attribute, '==', value));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((docSnap) => {
      if (collectionId === null) collectionId = docSnap.id;
      deleteDoc(docSnap.ref);
    });
    // If there are any subcollections we will have to delete them here
    if (collectionId !== null && collectionName === ENGINES) {
      deleteSubcollectionFromCollection(ENGINES, collectionId, SERPS);
    }
  } catch (e) {}
};

export const deleteDocumentFromCollection = async (
  collectionName,
  documentId
) => {
  await deleteDoc(doc(db, collectionName, documentId));
  if (collectionName === WEBSITES) {
    deleteSubcollectionFromCollection(WEBSITES, documentId, KEYWORDS);
  }
};

export const deleteSubcollectionFromCollection = async (
  parentCollection,
  childDocument,
  childCollection
) => {
  const subcollectionRef = collection(doc(db, parentCollection, childDocument), childCollection);
  const querySnapshot = await getDocs(subcollectionRef);
  querySnapshot.forEach((docSnap) => {
    deleteDoc(docSnap.ref);
  });
};

/**
 * @param  {string} collectionName
 * @param  {array} whereArrays - Array of arrays [['fieldName','queryOperator','value'],[]...]
 * @param {object} constraints {limit: number, orderBy: ['field', 'desc | asc']}
 * @returns {QuerySnapshot}
 */
export const queryCollection = async (
  collectionName,
  whereArrays,
  constraints
) => {
  const queryConstraints = [];

  if (whereArrays && whereArrays.length > 0) {
    for (let clauseArr of whereArrays) {
      queryConstraints.push(where(...clauseArr));
    }
  }

  if (constraints) {
    if (constraints.limit) {
      queryConstraints.push(firestoreLimit(constraints.limit));
    }
    if (constraints.orderBy) {
      queryConstraints.push(orderBy(...constraints.orderBy));
    }
  }

  const q = query(collection(db, collectionName), ...queryConstraints);
  return getDocs(q);
};

export const updateDocument = async (collectionId, docId, data) => {
  await setDoc(doc(db, collectionId, docId), data, { merge: true });

  return {
    success: true,
    data: { ...data, id: docId },
  };
};

/**
 *
 * @param {*} collectionId
 * @param {*} docId
 * @returns {object} { docData }
 */
export const getDocument = async (collectionId, docId) => {
  const docSnap = await getDoc(doc(db, collectionId, docId));
  const document = { ...docSnap.data(), id: docId };

  return {
    success: true,
    data: document,
  };
};

export const LIMIT = {
  KEYWORDS: 'keywords',
  ORGS: 'orgs',
  COMPETITORS: 'competitors',
  WEBSITES: 'domains',
  PAGE_AUDITS: 'pageAudits',
  ENGINES: 'engines',
  TECH_AUDITS: 'techAudits',
};

/**
 *
 * @param {string} limitKeyword
 * @param {string} orgId
 * @param {number} itemsBeingAdded
 * @returns
 */
export const checkLimits = async (limitKeyword, orgId, itemsBeingAdded = 1) => {

  const orgDocSnap = await getDoc(doc(db, 'orgs', orgId));
  const orgData = orgDocSnap.data()?.planFeatures;

  let planMax = 0;
  switch (limitKeyword) {
    case LIMIT.KEYWORDS:
      planMax = orgData?.max_keywords;
      break;
    case LIMIT.ORGS:
      planMax = orgData?.max_orgs;
      break;
    case LIMIT.COMPETITORS:
      planMax = orgData?.max_competitors;
      break;
    case LIMIT.WEBSITES:
      planMax = orgData?.max_domains;
      break;
    case LIMIT.PAGE_AUDITS:
      planMax = orgData?.max_page_audits;
      break;
    case LIMIT.ENGINES:
      planMax = orgData?.max_engines;
      break;
    case LIMIT.TECH_AUDITS:
      planMax = orgData?.max_tech_audit;
      break;
    default:
      break;
  }

  const limitsQuery = query(collection(db, 'limits'), where('orgId', '==', orgId));
  const limitsSnapshot = await getDocs(limitsQuery);
  const limitsDocId = limitsSnapshot.docs[0].id;
  const limitsData = limitsSnapshot.docs[0].data();
  const isAtLimit =
    Number(limitsData[limitKeyword]) + itemsBeingAdded > Number(planMax);

  const incrementLimitsBy = (amount) => {
    updateDoc(doc(db, 'limits', limitsDocId), {
      [limitKeyword]: increment(amount),
    });
  };

  const decrementLimitsBy = (amount) => {
    updateDoc(doc(db, 'limits', limitsDocId), {
      [limitKeyword]: increment(amount),
    });
  };

  if (itemsBeingAdded > 0) {
    return Promise.resolve({ isAtLimit, incrementLimitsBy });
  } else {
    return Promise.resolve({ isAtLimit, decrementLimitsBy });
  }
};

// Used during registration
export const getIncrementLimitsFunction = async (orgId) => {
  const limitsQuery = query(collection(db, 'limits'), where('orgId', '==', orgId));
  const limitsSnapshot = await getDocs(limitsQuery);
  const limitsDocId = limitsSnapshot.docs[0].id;

  console.log(`limitsDocId is ${limitsDocId}`)
  const incrementLimitsBy = (amount, limitKeyword) => {
    updateDoc(doc(db, 'limits', limitsDocId), {
      [limitKeyword]: increment(amount),
    });
  };

  return incrementLimitsBy;
};

export async function getFirebaseToken() {
  const auth = getAuth();
  const token = await auth.currentUser.getIdToken(/* forceRefresh */ true);
  return token;
}
